import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getCompanyIdFromToken = () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return (
      payload.company_id ||
      payload.companyId ||
      payload.companyID ||
      payload.org_id ||
      payload.organizationId ||
      payload.tenant_id ||
      null
    );
  } catch (error) {
    //console.error("Error decoding token:", error);
    return null;
  }
};

const getCompanyIdFromUserInfo = () => {
  try {
    const userInfo = localStorage.getItem("userInfo") || sessionStorage.getItem("userInfo");
    if (!userInfo) return null;
    const user = JSON.parse(userInfo);
    return user.company_id || user.companyId || user.organizationId || null;
  } catch (error) {
    //console.error("Error getting user info:", error);
    return null;
  }
};

const formatINR = (value) => {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const MinimumWageCompliance = () => {
  const [companyId, setCompanyId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [minimumWage, setMinimumWage] = useState("");

  const [loading, setLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const token = useMemo(
    () => localStorage.getItem("token") || sessionStorage.getItem("token"),
    []
  );

  useEffect(() => {
    const cid = getCompanyIdFromToken() || getCompanyIdFromUserInfo();
    if (!cid) return;
    setCompanyId(cid);

    const now = new Date();
    const monthName = MONTHS[now.getMonth()];
    setSelectedMonth(monthName);
    setSelectedYear(String(now.getFullYear()));
  }, []);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1];
  }, []);

  const fetchAvailableStates = async () => {
    if (!companyId || !selectedMonth || !selectedYear) return;

    setStatesLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/min-wage/states`,
        {
          company_id: companyId,
          month: selectedMonth,
          year: selectedYear,
        },{
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true
        }
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Failed to fetch states");
      }

      setAvailableStates(response.data.data.availableStates || []);

      // If the current selectedState is no longer available, reset it
      const incomingStates = response.data.data.availableStates || [];
      if (selectedState && !incomingStates.includes(selectedState)) {
        setSelectedState("");
      }
    } catch (e) {
      // keep UI usable even if state list fails
      setAvailableStates([]);
      //console.error("fetchAvailableStates error:", e);
    } finally {
      setStatesLoading(false);
    }
  };

  useEffect(() => {
    // Fetch state list whenever month/year changes
    fetchAvailableStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedMonth, selectedYear]);

  const handleCheck = async (stateOverride) => {
    setError("");
    setResult(null);

    if (!companyId) {
      setError("Company ID not found. Please log in again.");
      return;
    }
    if (!selectedMonth || !selectedYear) {
      setError("Please select month and year.");
      return;
    }

    const min = Number(minimumWage);
    if (Number.isNaN(min) || min <= 0) {
      setError("Please enter a valid minimum wage value.");
      return;
    }

    const stateToSend = stateOverride !== undefined ? stateOverride : selectedState;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/min-wage/compliance`,
        {
          company_id: companyId,
          month: selectedMonth,
          year: selectedYear,
          minimum_wage: min,
          ...(stateToSend ? { state: stateToSend } : {}),
        },{
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true
        }
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Compliance check failed");
      }

      setResult(response.data.data);
      setAvailableStates(response.data.data.availableStates || []);
      setSelectedState(response.data.data.appliedState || stateToSend || "");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Compliance check failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!result) return;

    const employees = result.employees || [];
    const companyIdToShow = result.company_id ?? companyId ?? "";

    // Build sheet with a professional layout:
    // - Title + metadata
    // - Frozen header row
    // - Auto-filter on the employee table
    // - Column widths and number formats for currency columns
    const metaMinimumWage = result.minimum_wage ?? minimumWage ?? "";
    const periodText = result?.period
      ? `${result.period.month} ${result.period.year}`
      : `${selectedMonth} ${selectedYear}`;

    const headerRowIndex = 5; // 0-based in aoa_to_sheet
    const dataStartRowIndex = headerRowIndex + 1;

    const aoa = [
      ["Minimum Wage Compliance Report"],
  
      ["Period", periodText],
      ["Minimum Wage", metaMinimumWage],
      ["", "", "", "", "", ""],
      ["Employee", "State", "Base Salary", "Minimum Wage", "Compliance", "Shortfall"],
      ...employees.map((e) => [
        e.employee_name,
        e.state ?? "",
        Number(e.base_salary ?? 0),
        Number(e.minimum_wage ?? 0),
        e.compliant ? "Compliant" : "Non-compliant",
        Number(e.shortfall ?? (e.compliant ? 0 : metaMinimumWage)),
      ]),
    ];

    const sheetName = "Minimum_Wage_Compliance";
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Column widths: A..F
    ws["!cols"] = [
      { wch: 32 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 18 },
    ];

    // Freeze panes: freeze rows up to and including header row
    // ySplit = number of frozen rows
    ws["!freeze"] = {
      xSplit: 0,
      ySplit: headerRowIndex + 1,
      topLeftCell: `A${dataStartRowIndex + 1}`,
    };

    // Auto-filter for the employee table
    ws["!autofilter"] = {
      ref: `A${headerRowIndex + 1}:F${headerRowIndex + 1 + employees.length}`,
    };

    // Apply number formats for currency columns (C, D, F)
    // Use Excel format: "#,##0.00" (safe across locales)
    const numberFormat = "#,##0.00";
    for (let i = 0; i < employees.length; i++) {
      const rowExcel = dataStartRowIndex + i + 1; // Excel row number (1-indexed)

      const baseCell = ws[`C${rowExcel}`];
      const minCell = ws[`D${rowExcel}`];
      const shortCell = ws[`F${rowExcel}`];

      if (baseCell && baseCell.t === "n") baseCell.z = numberFormat;
      if (minCell && minCell.t === "n") minCell.z = numberFormat;
      if (shortCell && shortCell.t === "n") shortCell.z = numberFormat;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const safeMonth = String(selectedMonth || result?.period?.month || "")
      .replaceAll(" ", "_")
      .trim();
    const safeYear = String(selectedYear || result?.period?.year || "");
    const fileName = `Minimum_Wage_Compliance_${safeMonth}_${safeYear}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  return (
    <Box>
      <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
            Minimum Wage Compliance
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="minwage-month-label">Month</InputLabel>
                <Select
                  labelId="minwage-month-label"
                  label="Month"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setResult(null);
                    setAvailableStates([]);
                    setSelectedState("");
                  }}
                >
                  {MONTHS.map((m) => (
                    <MenuItem value={m} key={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="minwage-year-label">Year</InputLabel>
                <Select
                  labelId="minwage-year-label"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(e.target.value);
                    setResult(null);
                    setAvailableStates([]);
                    setSelectedState("");
                  }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem value={String(y)} key={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={statesLoading || availableStates.length === 0}>
                <InputLabel id="minwage-state-label">State</InputLabel>
                <Select
                  labelId="minwage-state-label"
                  label="State"
                  value={selectedState}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedState(v);
                    if (Number(minimumWage) > 0) handleCheck(v);
                  }}
                >
                  <MenuItem value="">All States</MenuItem>
                  {availableStates.map((s) => (
                    <MenuItem value={s} key={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Minimum Wage (per month)"
                value={minimumWage}
                onChange={(e) => setMinimumWage(e.target.value)}
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => handleCheck()}
                disabled={loading || !selectedMonth || !selectedYear}
              >
                {loading ? <CircularProgress size={20} /> : "Check Compliance"}
              </Button>
            </Grid>
          </Grid>

          {error ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}

          {result ? (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={4}>
                  <Alert severity="info">
                    Period: <b>{result.period.month}</b> {result.period.year}
                  </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Alert severity="success">
                    Compliant: <b>{result.summary.compliantEmployees}</b> / {result.summary.totalEmployees}
                  </Alert>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Alert severity="error">
                    Non-compliant: <b>{result.summary.nonCompliantEmployees}</b> / {result.summary.totalEmployees}
                  </Alert>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
                Employee-wise Compliance
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleExportExcel}
                  disabled={!result || (result.employees || []).length === 0}
                >
                  Export
                </Button>
              </Box>

              <TableContainer sx={{ borderRadius: 3, boxShadow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Base Salary</TableCell>
                      <TableCell align="right">Minimum Wage</TableCell>
                      <TableCell>Compliance</TableCell>
                      <TableCell align="right">Shortfall</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.employees.map((e) => (
                      <TableRow key={e.employee_id}>
                        <TableCell>{e.employee_name}</TableCell>
                        <TableCell>{e.state || "-"}</TableCell>
                        <TableCell align="right">₹ {formatINR(e.base_salary)}</TableCell>
                        <TableCell align="right">₹ {formatINR(e.minimum_wage)}</TableCell>
                        <TableCell>
                          <Chip
                            label={e.compliant ? "Compliant" : "Non-compliant"}
                            color={e.compliant ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {e.compliant ? "-" : `₹ ${formatINR(e.shortfall)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MinimumWageCompliance;

